import { getAuth } from "firebase/auth";

import { getAPIURLv2, xget, xhttp } from "../Fetchv2.js";
import { MGSchema } from "../schemas/mg.schema.js";
import { PGST } from "../schemas/pg.schema-types.js";
import { PGSchema } from "../schemas/pg.schema.js";

export namespace ChatV2 {
	export interface ToolDeclaration {
		name: string;
		description: string;
		parameters: { type: string; properties: Record<string, any>; required?: string[] };
	}

	export interface ContentPart {
		text?: string;
		thought?: boolean;
		functionCall?: { name: string; args: Record<string, unknown> };
		functionResponse?: { name: string; response: any };
	}

	export interface Content {
		role: string;
		parts: ContentPart[];
	}

	export interface RequestBody {
		sessionId?: string;
		userMessage?: string;
		toolResponseParts?: ContentPart[];
		messages?: Array<{ role: "user" | "assistant"; content: string }>;
		activeTools?: string[];
		toolDeclarations?: ToolDeclaration[];
		activeViewName?: string;
		contents?: Content[];
	}

	export type Event =
		| { type: "text"; text: string }
		| { type: "function_calls"; calls: Array<{ name: string; args: Record<string, unknown> }>; assistantContent?: Content }
		| { type: "done" }
		| { type: "error"; message: string };
}

export namespace AiChat {
	export interface SessionSummary {
		id: string;
		title: string | null;
		created_at: string;
		updated_at: string;
	}

	export interface Message {
		id: string;
		role: "user" | "model" | "system";
		content: ChatV2.ContentPart[];
		created_at: string;
	}

	export interface SessionDetail extends SessionSummary {
		messages: Message[];
	}
}

export namespace AIAssistantSchema {
	export interface Params {
		"add-test-to-job": {
			title: string;
			company_name: string;
			domain: string;
			currentTestIds: number[];
			pgid: PGSchema.pages["id"];
			pid: PGSchema.jobs["job_id"];
		};
		"job-description-assistant": undefined;
		"job-description-from-free-text-assistant": undefined;
		"create-interview": {
			pgid: PGSchema.pages["id"];
			pid: PGSchema.jobs["job_id"];
		};
		"create-test": {
			pgid: PGSchema.pages["id"];
			pid: PGSchema.jobs["job_id"];
			uid: PGSchema.users["id"];
			questionCount?: number;
			comments?: string;
		};
		"final-candidates": {
			pgid: PGSchema.pages["id"];
			pid: PGSchema.jobs["job_id"];
			uids: PGSchema.users["id"][];
		};
		"create-interview-questions": {
			pgid: PGSchema.pages["id"];
			pid: PGSchema.jobs["job_id"];
			interviewId: PGSchema.interviews["id"];
			questionCount?: number;
			comments?: string;
		};
		"create-test-questions": {
			pgid: PGSchema.pages["id"];
			pid: PGSchema.jobs["job_id"];
			testId: MGSchema.tests["_id"];
			questionCount?: number;
			comments?: string;
		};
		"get-test-description": {
			complete_id: string;
		};
		"generate-more-questions-assistant": undefined;
		"improve-question-wording-assistant": undefined;
		example: any;
	}
}
export class AI {
	/**
	 * Turn-based SSE proxy for ai-assistant-v2.
	 * Streams one Gemini turn. The callback receives typed events.
	 * When a `function_calls` event arrives the caller should execute the tools,
	 * append the assistant content + tool responses to `body.contents`, and call again.
	 */
	async chatV2(pgid: string, token: string, body: ChatV2.RequestBody, onEvent: (event: ChatV2.Event) => void, signal?: AbortSignal): Promise<void> {
		const idToken = (await getAuth().currentUser?.getIdToken()) || "no-auth";
		const url = new URL(getAPIURLv2() + `/pages/${pgid}/ai/chat-v2`);

		const response = await fetch(url.href, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `bearer ${idToken}`,
				"app-token": token,
				"Cache-Control": "no-cache"
			},
			body: JSON.stringify(body),
			signal
		});

		if (!response.ok || !response.body) {
			onEvent({ type: "error", message: `HTTP ${response.status}` });
			return;
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = "";

		while (true) {
			const { done, value } = await reader.read();
			if (done) break;
			buffer += decoder.decode(value, { stream: true });

			const lines = buffer.split("\n\n");
			buffer = lines.pop() ?? "";

			for (const line of lines) {
				if (!line.startsWith("data: ")) continue;
				try {
					const event = JSON.parse(line.slice(6)) as ChatV2.Event;
					onEvent(event);
				} catch {
					// skip malformed chunk
				}
			}
		}

		// Flush remaining buffer
		if (buffer.startsWith("data: ")) {
			try {
				const event = JSON.parse(buffer.slice(6)) as ChatV2.Event;
				onEvent(event);
			} catch {}
		}
	}

	listSessions(pgid: string, token: string) {
		return xget<{ items: AiChat.SessionSummary[] }>(`/pages/${pgid}/ai/sessions`, undefined, { headers: { "app-token": token } });
	}

	createSession(pgid: string, token: string) {
		return xhttp<AiChat.SessionDetail>(`/pages/${pgid}/ai/sessions`, "POST", {}, { headers: { "app-token": token } });
	}

	getSession(pgid: string, token: string, sessionId: string) {
		return xget<AiChat.SessionDetail>(`/pages/${pgid}/ai/sessions/${sessionId}`, undefined, { headers: { "app-token": token } });
	}

	updateSession(pgid: string, token: string, sessionId: string, patch: { title?: string }) {
		return xhttp<AiChat.SessionDetail>(`/pages/${pgid}/ai/sessions/${sessionId}`, "PUT", patch, { headers: { "app-token": token } });
	}

	deleteSession(pgid: string, token: string, sessionId: string) {
		return xhttp<{ deleted: boolean }>(`/pages/${pgid}/ai/sessions/${sessionId}`, "DELETE", {}, { headers: { "app-token": token } });
	}

	AIAssistant<K extends keyof AIAssistantSchema.Params>(pgid: string, token: string, message: string, assistantId: K, params?: AIAssistantSchema.Params[K]) {
		return xhttp<{
			role: string;
			content: string;
		}>(
			`/pages/${pgid}/ai`,
			"POST",
			{
				message,
				payload: {
					assistantId,
					params
				}
			},
			{
				headers: {
					"app-token": token
				}
			}
		);
	}

	chatCompletion(pgid: string, message: string, history: PGST.assistant_history[], token: string, callback: (event: string) => void, route?: string) {
		return xhttp<{
			role: string;
			content: string;
		}>(
			`/pages/${pgid}/ai/chat`,
			"POST",
			{
				history,
				message,
				...(route !== undefined ? { route } : {})
			},
			{
				server_send_events: (event: string) => {
					console.log(event);
					callback(event);
				},
				headers: {
					"app-token": token
				}
			}
		);
	}

	/**
	 * Del lado de la empresa
	 * Genera un {@link PGSchema.users} a partir de un archivo PDF
	 */
	generateCV(pgid: string, file: File, token: string) {
		const formData = new FormData();
		formData.append("file", file);
		window.any = {
			email: "herberth@grupoconectate2.com",
			dpi: "271274794010"
		};
		if (process.env.NODE_ENV === "development") {
			return [
				{
					success: true,
					extracted_cv: {
						...((window.any as any) || {
							email: "herberth@grupoconectate.com",
							dpi: "2712747940101"
						}),
						name: "Herberth",
						last_name: "Obregón",

						academy: [
							{
								co_name: "Universidad de San Carlos de Guatemala",
								descrip: "Desarrollo del portal de Orientación vocacional de la Universidad de San Carlos de Guatemala. El portal esta alojado en https://vocacional.usac.edu.gt",
								finish_time: 1633046400,
								init_time: 1551398400,
								title: "Full Stack Developer",
								type: 0,
								work_id: 1762265108943,
								country_id: 90,
								state_id: 1529
							},
							{
								co_name: "Conectate",
								descrip:
									"Conectate Today es la mejor red social profesional que crea conexiones entre personas y ayuda a tu empresa a conectarse con las personas que necesitas a través de su portal de empleo y software de gestión de personal unificando todos los portales de empleo en 1 solo para mejor manejo del nuevo talento.",
								finish_time: 1762214400,
								init_time: 1483228800,
								title: "CTO & Full-stack Developer",
								type: 0,
								work_id: 1762265108944,
								country_id: 90,
								state_id: 1529
							},
							{
								co_name: "Facultad de Ingeniería USAC",
								descrip:
									'Creación de la aplicación "Ingeniería USAC" en la cual se pueden ver las noticias mas importantes de la Facultad de Ingeniería de la Universidad de San Carlos de Guatemala, Asignarse cursos y recibir notificaciones de las clases asignadas',
								finish_time: 1483228800,
								init_time: 1462060800,
								title: "Android Developer",
								type: 0,
								work_id: 1762265108945,
								country_id: 90,
								state_id: 1529
							},
							{
								co_name: "Elecciones Guatemala 2015",
								descrip:
									"Desarrollo de la aplicación elecciones Guatemala 2015 en la cual se podrían hacer encuestas y ver los resultados de las mismas. Ver en tiempo real los resultados de las elecciones en la 1era y 2da vuelta.",
								finish_time: 1443657600,
								init_time: 1427846400,
								title: "Full-Stack Developer",
								type: 0,
								work_id: 1762265108946,
								country_id: 90,
								state_id: 1529
							},
							{
								co_name: "Universidad de San Carlos de Guatemala",
								descrip: "",
								finish_time: 1762214400,
								init_time: 1356998400,
								title: "Ingeniería en Ciencias y Sistemas",
								type: 1,
								work_id: 1762265108947,
								country_id: 90,
								state_id: 1529
							},
							{
								co_name: "Colegio San José de los Infantes",
								descrip: "",
								finish_time: 1349049600,
								init_time: 978307200,
								title: "Bach. en Ciencias y Letras con Orientacion en Computación",
								type: 1,
								work_id: 1762265108948,
								country_id: 90,
								state_id: 1529
							}
						],
						skills: [
							{
								n: "sql",
								p: 85
							},
							{
								n: "python",
								p: 80
							},
							{
								n: "nosql",
								p: 75
							},
							{
								n: "javascript",
								p: 90
							},
							{
								n: "android",
								p: 85
							},
							{
								n: "flask",
								p: 70
							},
							{
								n: "react",
								p: 75
							},
							{
								n: "polymer",
								p: 65
							},
							{
								n: "angular",
								p: 70
							},
							{
								n: "vuejs",
								p: 65
							},
							{
								n: "nginx",
								p: 70
							},
							{
								n: "c++",
								p: 75
							},
							{
								n: "mysql",
								p: 80
							},
							{
								n: "postgresql",
								p: 80
							},
							{
								n: "sql server",
								p: 70
							},
							{
								n: "google cloud",
								p: 85
							},
							{
								n: "aws",
								p: 80
							},
							{
								n: "firebase",
								p: 75
							},
							{
								n: "inteligencia emocional",
								p: 70
							},
							{
								n: "trabajo en equipo",
								p: 75
							},
							{
								n: "scrum",
								p: 70
							},
							{
								n: "node",
								p: 80
							},
							{
								n: "webpack",
								p: 70
							},
							{
								n: "java",
								p: 75
							},
							{
								n: "lit",
								p: 65
							}
						],
						lngs: [
							{
								id: 1,
								lvl: 2
							},
							{
								id: 27,
								lvl: 4
							}
						],
						title: "Ingeniero en Ciencias y Sistemas",
						about_me:
							"Ingeniero en Ciencias y Sistemas, dedicado y apasionado, con más de 10 años de experiencia trabajando con tecnologías en la nube. Tengo 30 años y nací el 16 de abril de 1995. Poseo un conocimiento integral de los protocolos y procedimientos de seguridad en la nube. Tengo experiencia en la prestación de servicios escalables, incluyendo computación en la nube, redes, desarrollo de aplicaciones integrales y soporte en diversas tecnologías. Soy experto en aplicar sólidos conocimientos y experiencia para analizar, desarrollar e implementar. Tengo un historial comprobado de rendimiento superior y una clara dedicación a la seguridad de aplicaciones en la nube.",
						birthday: 798144000,
						civil_status_id: 0,
						dir: "14 Calle B Zona 7 Mixco",
						gender: true,
						studies: 3,
						tel: [
							{
								code: 502,
								phone: "59532032"
							}
						],
						urls: [
							{
								t: 0,
								txt: "https://vocacional.usac.edu.gt"
							}
						],
						wa_phone: "50259532032"
					},
					email_exists: null
				},
				null
			] as any as ReturnType<
				typeof xhttp<{
					success: boolean;
					extracted_cv: PGSchema.users;
				}>
			>;
		}

		return xhttp<{
			success: boolean;
			extracted_cv: PGSchema.users;
		}>(`/pages/${pgid}/ai/generate-cv`, "POST", formData, { headers: { "app-token": token } });
	}

	/**
	 * Del lado del usuario
	 * Genera un {@link PGSchema.users} a partir de un archivo PDF
	 */
	generateCVUser(file: File | Blob) {
		const formData = new FormData();
		formData.append("file", file);

		return xhttp<{
			success: boolean;
			extracted_cv: PGSchema.users;
		}>(`/users/me/ai/generate-cv`, "POST", formData);
	}
}
